import { authenticatedFetch, ForbiddenError } from "@src/features/auth";

/**
 * Exchanges the signed-in user's Vision Auth token for a Storybook session.
 *
 * The Storybook site is served from this app's own origin, so the cookie the
 * session endpoint returns is a plain first-party cookie: the CloudFront
 * function guarding `/storybook` reads it on every subsequent request.
 *
 * Paths must match the CDK constants in
 * `apps/a3s-cloud/lib/storybook/storybook-gate.ts`.
 */

export const STORYBOOK_PATH = "/storybook/";
export const STORYBOOK_SESSION_PATH = "/storybook-session";

/**
 * Whether this deployment publishes Storybook.
 *
 * Mirrors `enableStorybook` in `apps/a3s-cloud`, which is set for the
 * integration environment only. Anywhere else the paths below are not mounted
 * on the distribution at all.
 */
export function isStorybookPublished(environmentKey: string): boolean {
  return environmentKey === "int";
}

/**
 * The only email domain the session endpoint issues sessions to. Mirrors
 * `ALLOWED_EMAIL_DOMAIN` in `apps/a3s-cloud`.
 *
 * Checked here only so staff without a Visioni address are not offered a link
 * that would refuse them. The endpoint decides for real, against the address it
 * reads from the identity provider rather than anything the browser claims.
 */
export const STORYBOOK_EMAIL_DOMAIN = "visioni.com.au";

export function hasStorybookEmailDomain(email: string | undefined): boolean {
  if (email === undefined) {
    return false;
  }
  const at = email.lastIndexOf("@");
  return (
    at !== -1 &&
    email
      .slice(at + 1)
      .trim()
      .toLowerCase() === STORYBOOK_EMAIL_DOMAIN
  );
}

/**
 * Resolves where to send the browser once a session exists.
 *
 * The gate function puts the originally requested URL in `?return=`, so the
 * value is attacker-influenced. Resolving it against the current origin and
 * keeping only the path means a crafted value such as `//evil.example/storybook/`
 * resolves off-origin and falls back to the Storybook landing page.
 */
export function resolveStorybookTarget(requested: string | string[] | undefined, origin: string): string {
  const candidate = Array.isArray(requested) ? requested[0] : requested;

  if (candidate === undefined) {
    return STORYBOOK_PATH;
  }

  const url = new URL(candidate, origin);

  return url.origin === origin && url.pathname.startsWith(STORYBOOK_PATH) ?
      `${url.pathname}${url.search}`
    : STORYBOOK_PATH;
}

export type StorybookSessionResult = { kind: "opened" } | { kind: "denied" } | { kind: "failed"; detail: string };

/** Requests a Storybook session cookie for the signed-in user. */
export async function openStorybookSession(): Promise<StorybookSessionResult> {
  try {
    const response = await authenticatedFetch(STORYBOOK_SESSION_PATH, { method: "POST" });

    if (!response.ok) {
      return { kind: "failed", detail: `The session endpoint answered HTTP ${String(response.status)}.` };
    }

    // Where Storybook is not deployed, this path falls through to the SPA and
    // returns the app's own HTML with a 200. Requiring the endpoint's JSON
    // stops that from being mistaken for a session.
    if (!(response.headers.get("content-type") ?? "").includes("application/json")) {
      return { kind: "failed", detail: "Storybook is not deployed in this environment." };
    }

    return { kind: "opened" };
  } catch (error) {
    // The session endpoint answers 403 when the account has no `a3s:support`
    // role, which `authenticatedFetch` surfaces as a thrown ForbiddenError.
    if (error instanceof ForbiddenError) {
      return { kind: "denied" };
    }
    return { kind: "failed", detail: error instanceof Error ? error.message : "Unknown error" };
  }
}
